import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AppRoot, LoginOkRoute } from '@/app/AppRoot';
import { AppLayout } from '@/app/layouts/AppLayout';
import { NotFoundPage } from '@/app/pages/NotFoundPage';
import { BirdEyePage } from '@/features/bird-eye/pages/BirdEyePage';
import { CalendarPage } from '@/features/calendar/pages/CalendarPage';
import { DashboardPage } from '@/features/dashboard/pages/DashboardPage';
import { MessagesPage } from '@/features/messages/pages/MessagesPage';
import { EmailPage } from '@/features/email/pages/EmailPage';
import { TriagePage } from '@/features/triage/pages/TriagePage';
import { CallsPage } from '@/features/calls/pages/CallsPage';
import { PatientsListPage } from '@/features/patients/pages/PatientsListPage';
import { PatientProfilePage } from '@/features/patients/pages/PatientProfilePage';
import { PatientProfileTabRedirect } from '@/features/patients/pages/PatientProfileTabRedirect';
import { PatientUrgencyPublicPage } from '@/features/patient-urgency/pages/PatientUrgencyPublicPage';
import { PlanPagePublicPage } from '@/features/plan-page/pages/PlanPagePublicPage';
import { BookingPagePublicPage } from '@/features/booking/pages/BookingPagePublicPage';
import { OnlineBookingsPage } from '@/features/booking/pages/OnlineBookingsPage';
import { PatientInvisalignPublicPageRoute } from '@/features/patient-invisalign/pages/PatientInvisalignPublicPage';
import { InvTextConsentPublicPage } from '@/features/patient-invisalign/pages/InvTextConsentPublicPage';
import { FormsListPage } from '@/features/forms/pages/FormsListPage';
import { SentFormsPage } from '@/features/forms/pages/SentFormsPage';
import { FormBuilderPage } from '@/features/forms/pages/FormBuilderPage';
import { SettingsProvidersPage } from '@/features/settings/pages/SettingsProvidersPage';
import { SettingsGeneralInfoPage } from '@/features/settings/pages/SettingsGeneralInfoPage';
import { SettingsOperatoriesPage } from '@/features/settings/pages/SettingsOperatoriesPage';
import { SettingsInsuranceNetworkPage } from '@/features/settings/pages/SettingsInsuranceNetworkPage';
import { SettingsAppointmentReasonsPage } from '@/features/settings/pages/SettingsAppointmentReasonsPage';
import { SettingsOnlineBookingPage } from '@/features/settings/pages/SettingsOnlineBookingPage';
import { SettingsCalendarInsightsPage } from '@/features/settings/pages/SettingsCalendarInsightsPage';
import { SettingsBrainPage } from '@/features/settings/pages/SettingsBrainPage';
import { SettingsTrainElvaPage } from '@/features/settings/pages/SettingsTrainElvaPage';
import { SettingsKnowledgeBasePage } from '@/features/settings/pages/SettingsKnowledgeBasePage';
import { SettingsChannelPage } from '@/features/settings/pages/SettingsChannelPage';
import { SettingsReceptionistPage } from '@/features/settings/pages/SettingsReceptionistPage';
import { SettingsPhoneNumbersPage } from '@/features/settings/pages/SettingsPhoneNumbersPage';
import { SettingsCancellationPolicyPage } from '@/features/settings/pages/SettingsCancellationPolicyPage';
import {
  SettingsEmailOauthCallbackPage,
  SettingsGeneralInfoLegacyRedirect,
} from '@/features/settings/pages/SettingsEmailOauthCallbackPage';
import { AiTeamPage } from '@/features/ai-team/pages/AiTeamPage';
import { AiReceptionistPage } from '@/features/ai-team/pages/AiReceptionistPage';
import { AppointmentRemindersPage } from '@/features/ai-team/pages/AppointmentRemindersPage';
import { RecallAgentPage } from '@/features/ai-team/pages/RecallAgentPage';
import { GapFillerPage } from '@/features/ai-team/pages/GapFillerPage';
import { GapDetailPage } from '@/features/ai-team/pages/GapDetailPage';
import { TreatmentCoordinatorPage } from '@/features/ai-team/pages/TreatmentCoordinatorPage';
import { TiOpportunityPage } from '@/features/treatment-intel/pages/TiOpportunityPage';
import { TiCampaignsPage } from '@/features/treatment-intel/pages/TiCampaignsPage';
import { TiCampaignBuilderPage } from '@/features/treatment-intel/pages/TiCampaignBuilderPage';
import { TiCampaignReportPage } from '@/features/treatment-intel/pages/TiCampaignReportPage';
import { InvisalignCoordinatorPage } from '@/features/ai-team/pages/InvisalignCoordinatorPage';
import { ArCoordinatorPage } from '@/features/ai-team/pages/ArCoordinatorPage';
import { CommunicationLogPage } from '@/features/communications/pages/CommunicationLogPage';
import { CollectionsPage } from '@/features/collections/pages/CollectionsPage';
import { CollectionsFollowUpsPage } from '@/features/collections/pages/CollectionsFollowUpsPage';
import { CollectionsLinksPage } from '@/features/collections/pages/CollectionsLinksPage';
import { CollectionsFollowUpBuilderPage } from '@/features/collections/pages/CollectionsFollowUpBuilderPage';
import { SettingsLayout } from '@/features/settings/SettingsLayout';
import {
  RemindersEditorRedirect,
  RemindersMessagesPage,
  RemindersTestPage,
} from '@/features/settings/pages/RemindersEditorPages';
import { SettingsRemindersPage } from '@/features/settings/pages/SettingsRemindersPage';
import { SettingsRecallAgentPage } from '@/features/settings/pages/SettingsRecallAgentPage';
import { SettingsGapFillerPage } from '@/features/settings/pages/SettingsGapFillerPage';
import { GapRulePage } from '@/features/settings/pages/GapRulePage';
import { GapOfferPage } from '@/features/settings/pages/GapOfferPage';
import { SettingsTcPage } from '@/features/settings/pages/SettingsTcPage';
import { SettingsArPage } from '@/features/settings/pages/SettingsArPage';
import { SettingsInvisPage } from '@/features/settings/pages/SettingsInvisPage';
import { SettingsInvisProgrammePage } from '@/features/settings/pages/SettingsInvisProgrammePage';
import { RecallListPage, RecallResolvePage } from '@/features/recall/pages/RecallListPage';
import {
  RecallAudiencePage,
  RecallMessagesPage,
  RecallTestPage,
} from '@/features/recall/pages/RecallBuilderPages';
import { RecallReportPage } from '@/features/recall/pages/RecallReportPage';
import { WarmTransferPage } from '@/features/warm-transfer/pages/WarmTransferPage';

export const router = createBrowserRouter([
  {
    element: <AppRoot />,
    children: [
      {
        path: '/loginok',
        element: <LoginOkRoute />,
      },
      {
        path: '/treatment',
        element: <PatientUrgencyPublicPage />,
      },
      {
        // Aligner-text consent (amendment 33 §2), outside the app shell:
        // {practice}.elva.ai/p/texts/{code}. A two-segment path so it ranks
        // above the single-segment plan page at /p/:code.
        path: '/p/texts/:code',
        element: <InvTextConsentPublicPage />,
      },
      {
        // The patient-facing plan page (amendment 27 §6, staging fix 29 §1),
        // outside the app shell: {practice}.elva.ai/p/{code}.
        path: '/p/:code',
        element: <PlanPagePublicPage />,
      },
      {
        path: '/invisalign',
        element: <PatientInvisalignPublicPageRoute />,
      },
      {
        // The public booking page (spec 50 §1), outside the app shell:
        // book.elva.ai/{location}.
        path: '/book/:code',
        element: <BookingPagePublicPage />,
      },
      {
        path: '/settings/email-oauth/callback',
        element: <SettingsEmailOauthCallbackPage />,
      },
      {
        path: '/',
        element: <AppLayout />,
        children: [
          // Spec 17 §2: /dashboard is the app's landing route, and it replaces
          // whatever loaded first. Calendar keeps a route of its own rather than
          // owning `/`.
          {
            index: true,
            element: <Navigate to="/dashboard" replace />,
          },
          {
            path: 'dashboard',
            element: <DashboardPage />,
          },
          {
            path: 'calendar',
            element: <CalendarPage />,
          },
          {
            path: 'bird-eye',
            element: <BirdEyePage />,
          },
          {
            path: 'ai-team',
            element: <AiTeamPage />,
          },
          {
            path: 'ai-team/receptionist',
            element: <AiReceptionistPage />,
          },
          {
            path: 'ai-team/reminders',
            element: <AppointmentRemindersPage />,
          },
          {
            path: 'ai-team/recall',
            element: <RecallAgentPage />,
          },
          {
            path: 'ai-team/invisalign',
            element: <InvisalignCoordinatorPage tab="overview" />,
          },
          {
            path: 'ai-team/invisalign/prospects',
            element: <InvisalignCoordinatorPage tab="prospects" />,
          },
          {
            path: 'ai-team/invisalign/line',
            element: <InvisalignCoordinatorPage tab="line" />,
          },
          {
            path: 'ai-team/gap-filler',
            element: <GapFillerPage />,
          },
          {
            path: 'ai-team/gap-filler/:gapId',
            element: <GapDetailPage />,
          },
          {
            path: 'ai-team/treatment',
            element: <TreatmentCoordinatorPage />,
          },
          {
            path: 'ai-team/ar',
            element: <ArCoordinatorPage />,
          },
          {
            path: 'recall',
            element: <RecallListPage />,
          },
          // The builder (spec 21): three pages, and the SAME three when editing.
          // One component per page for add and edit — if they are two, they drift.
          { path: 'recall/new/audience', element: <RecallAudiencePage /> },
          { path: 'recall/new/messages', element: <RecallMessagesPage /> },
          { path: 'recall/new/test', element: <RecallTestPage /> },
          // `sequence` is the old page 2. It redirects rather than 404s, because
          // the word is still in people's bookmarks and in the old boards.
          { path: 'recall/new/sequence', element: <Navigate to="/recall/new/messages" replace /> },
          {
            path: 'recall/:id/report',
            element: <RecallReportPage />,
          },
          { path: 'recall/:id/audience', element: <RecallAudiencePage /> },
          { path: 'recall/:id/messages', element: <RecallMessagesPage /> },
          { path: 'recall/:id/test', element: <RecallTestPage /> },
          // `/recall/<id>` alone resolves by state (§4) rather than opening an
          // editor for a running recall.
          { path: 'recall/:id', element: <RecallResolvePage /> },
          {
            path: 'treatment-intelligence',
            element: <TiOpportunityPage />,
          },
          {
            path: 'treatment-intelligence/campaigns',
            element: <TiCampaignsPage />,
          },
          // The TC campaign builder — three pages over one draft (amendment 27 §5).
          {
            path: 'treatment-intelligence/campaigns/new',
            element: <TiCampaignBuilderPage page="audience" />,
          },
          {
            path: 'treatment-intelligence/campaigns/new/messages',
            element: <TiCampaignBuilderPage page="messages" />,
          },
          {
            path: 'treatment-intelligence/campaigns/new/test',
            element: <TiCampaignBuilderPage page="test" />,
          },
          {
            path: 'treatment-intelligence/campaigns/:id/edit',
            element: <TiCampaignBuilderPage page="audience" />,
          },
          {
            path: 'treatment-intelligence/campaigns/:id/edit/messages',
            element: <TiCampaignBuilderPage page="messages" />,
          },
          {
            path: 'treatment-intelligence/campaigns/:id/edit/test',
            element: <TiCampaignBuilderPage page="test" />,
          },
          {
            path: 'treatment-intelligence/campaigns/:id/report',
            element: <TiCampaignReportPage />,
          },
          {
            path: 'collections',
            element: <CollectionsPage />,
          },
          { path: 'collections/follow-ups', element: <CollectionsFollowUpsPage /> },
          { path: 'collections/links', element: <CollectionsLinksPage /> },
          // The follow-up builder (spec 28 §4): three pages, the SAME three when editing.
          { path: 'collections/follow-ups/new', element: <CollectionsFollowUpBuilderPage page="audience" /> },
          { path: 'collections/follow-ups/new/messages', element: <CollectionsFollowUpBuilderPage page="messages" /> },
          { path: 'collections/follow-ups/new/test', element: <CollectionsFollowUpBuilderPage page="test" /> },
          { path: 'collections/follow-ups/:id/edit', element: <CollectionsFollowUpBuilderPage page="audience" /> },
          { path: 'collections/follow-ups/:id/edit/messages', element: <CollectionsFollowUpBuilderPage page="messages" /> },
          { path: 'collections/follow-ups/:id/edit/test', element: <CollectionsFollowUpBuilderPage page="test" /> },
          {
            path: 'communications',
            element: <CommunicationLogPage />,
          },
          {
            path: 'messages',
            element: <MessagesPage />,
          },
          {
            path: 'email',
            element: <EmailPage />,
          },
          {
            path: 'triage',
            element: <TriagePage />,
          },
          {
            path: 'calls',
            element: <CallsPage />,
          },
          {
            path: 'warm-transfer',
            element: <WarmTransferPage />,
          },
          {
            path: 'patients',
            element: <PatientsListPage />,
          },
          {
            path: 'patients/:patientId',
            children: [
              {
                index: true,
                element: <PatientProfileTabRedirect />,
              },
              {
                path: ':tab',
                element: <PatientProfilePage />,
              },
            ],
          },
          {
            path: 'forms',
            element: <SentFormsPage />,
          },
          {
            path: 'forms/templates',
            element: <FormsListPage />,
          },
          {
            path: 'forms/create',
            element: <FormBuilderPage />,
          },
          {
            path: 'online-bookings',
            element: <OnlineBookingsPage />,
          },
          // The Gap Filler rule + offer editors (amendment 26 §5–§6) are full width
          // with no settings sub-menu, like the reminders editors below.
          {
            path: 'settings/ai/gap-filler/rules/:ruleId',
            element: <GapRulePage />,
          },
          {
            path: 'settings/ai/gap-filler/offer/:page',
            element: <GapOfferPage />,
          },
          // The reminders editors (amendment 25 §1) sit OUTSIDE SettingsLayout on
          // purpose: they are full width with no settings sub-menu. The editor is
          // the whole task, and a column of other settings beside it is an
          // invitation to leave halfway through.
          {
            path: 'settings/ai/reminders/after/:procedure/messages',
            element: <RemindersMessagesPage />,
          },
          {
            path: 'settings/ai/reminders/after/:procedure/test',
            element: <RemindersTestPage />,
          },
          {
            path: 'settings/ai/reminders/after/:procedure',
            element: <RemindersEditorRedirect />,
          },
          {
            path: 'settings/ai/reminders/:section/messages',
            element: <RemindersMessagesPage />,
          },
          {
            path: 'settings/ai/reminders/:section/test',
            element: <RemindersTestPage />,
          },
          // The Invisalign programme (amendment 33 §1) is full width, no settings
          // sub-nav — the editor is the whole task.
          {
            path: 'settings/ai/invisalign/programme',
            element: <SettingsInvisProgrammePage />,
          },
          {
            path: 'settings/ai/invisalign/programme/:step',
            element: <SettingsInvisProgrammePage />,
          },
          // Settings (spec 15). Every page renders inside the 258px settings
          // column; routes match §2.2 exactly. Rows whose page is not built yet
          // routes to a real page — spec 16 §2.1 retired the last placeholder.
          {
            path: 'settings',
            element: <SettingsLayout />,
            children: [
              { index: true, element: <Navigate to="/settings/general" replace /> },
              { path: 'general', element: <SettingsGeneralInfoPage /> },
              { path: 'general-info', element: <SettingsGeneralInfoLegacyRedirect /> },
              { path: 'providers', element: <SettingsProvidersPage /> },
              { path: 'operatories', element: <SettingsOperatoriesPage /> },
              { path: 'insurance', element: <SettingsInsuranceNetworkPage /> },
              { path: 'phone-numbers', element: <SettingsPhoneNumbersPage /> },
              { path: 'appointment-reasons', element: <SettingsAppointmentReasonsPage /> },
              { path: 'online-booking', element: <SettingsOnlineBookingPage /> },
              { path: 'calendar-insights', element: <SettingsCalendarInsightsPage /> },
              { path: 'cancellation-policy', element: <SettingsCancellationPolicyPage /> },
              { path: 'ai/general', element: <SettingsBrainPage /> },
              { path: 'ai/train', element: <SettingsTrainElvaPage /> },
              { path: 'ai/knowledge-base', element: <SettingsKnowledgeBasePage /> },
              { path: 'ai/channels/call', element: <SettingsChannelPage channel="call" /> },
              { path: 'ai/channels/sms', element: <SettingsChannelPage channel="sms" /> },
              { path: 'ai/channels/email', element: <SettingsChannelPage channel="email" /> },
              // Spec 38: the Email settings (with the Gmail card) answer on both URLs.
              { path: 'ai/email', element: <SettingsChannelPage channel="email" /> },
              { path: 'ai/receptionist', element: <SettingsReceptionistPage /> },
              // Spec 16 §2.1 fully supersedes spec 15 §1.2: every agent row now
              // routes to a real page, and nothing is left on the placeholder list.
              { path: 'ai/reminders', element: <SettingsRemindersPage /> },
              { path: 'ai/recall', element: <SettingsRecallAgentPage /> },
              { path: 'ai/gap-filler', element: <SettingsGapFillerPage /> },
              { path: 'ai/treatment-coordinator', element: <SettingsTcPage /> },
              { path: 'ai/invisalign', element: <SettingsInvisPage /> },
              { path: 'ai/accounts-receivable', element: <SettingsArPage /> },
            ],
          },
          // Any unknown address renders the in-shell Page-not-found (spec 49 §2),
          // never React Router's raw developer error screen.
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
]);
